import React from "react";

/**
 * Called once per animation frame with the frame's timestamp (ms, as passed to
 * requestAnimationFrame) and the seconds since the previous frame.
 */
export type FrameCallback = (now: number, dt: number) => void;

// Longest step a frame reports, so a stall (a hidden tab, a debugger pause)
// doesn't make simulations jump. The prototype clamps every loop to this.
const MAX_DT = 0.1;

const callbacks = new Set<FrameCallback>();
let rafId: number | null = null;
let last: number | null = null;

const tick = (now: number) => {
    const dt = last === null ? 0 : Math.min((now - last) / 1000, MAX_DT);
    last = now;
    // Schedule first and isolate each callback, so one that throws neither
    // stops the loop nor skips the others. unsubscribe cancels it when empty.
    rafId = requestAnimationFrame(tick);
    for (const callback of callbacks) {
        try {
            callback(now, dt);
        } catch (error) {
            // Rethrown outside the loop so it still reaches the error handlers
            // (reportError is too new for the Safari 14 target)
            setTimeout(() => {
                throw error;
            });
        }
    }
};

/**
 * Run a callback on the page's single animation loop. The loop runs only while
 * something is subscribed. Returns the unsubscribe function.
 */
export const subscribeFrame = (callback: FrameCallback) => {
    callbacks.add(callback);
    if (rafId === null) {
        last = null;
        rafId = requestAnimationFrame(tick);
    }
    return () => {
        callbacks.delete(callback);
        if (callbacks.size === 0 && rafId !== null) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    };
};

/**
 * Subscribe a component to the page's animation loop while `enabled` is true.
 * The latest callback is always the one called, so it may close over props.
 */
export const useFrame = (callback: FrameCallback, enabled = true) => {
    const callbackRef = React.useRef(callback);
    React.useLayoutEffect(() => {
        callbackRef.current = callback;
    });

    React.useEffect(() => {
        if (!enabled) return;
        return subscribeFrame((now, dt) => callbackRef.current(now, dt));
    }, [enabled]);
};
